import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { SwipeableRow } from '@shared/ui/primitives/SwipeableRow';
import { useModuleStore } from '@shared/stores/moduleStore';
import {
  fetchAccessInvites,
  fetchAccessEntryTypesForInvite,
  type AccessInvite,
} from '@features/access-invites/owner/api';
import { AccessEntryTypeIcon, AccessEntryTypeQuickIcon } from '@features/access-invites/owner/AccessEntryTypeIcon';
import { ACCESS_INVITES_QUERY_KEY } from '@features/access-invites/owner/accessInviteCache';
import { filterUpcomingInvites } from '@features/access-invites/owner/inviteFilters';
import {
  buildInviteSwipeActions,
} from '@features/access-invites/owner/inviteSwipeActions';
import { useAccessInviteCancelConfirm } from '@features/access-invites/owner/useAccessInviteCancelConfirm';
import { useCancelAccessInviteMutation } from '@features/access-invites/owner/useCancelAccessInviteMutation';
import { NewAccessInviteSheet } from '@features/access-invites/owner/NewAccessInvite';
import { homeTileButtonClass, TileLabel } from '@features/dashboard/TileIcon';
import { WidgetCard } from './WidgetCard';
import { widgetTitle, type WidgetProps } from './registry';

const WIDGET_INVITE_LIMIT = 3;
const TIME_FMT = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
});

function fmtTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : TIME_FMT.format(d);
}

function inviteMeta(invite: {
  visitor_phone?: string;
  reference?: string;
  valid_from?: string | null;
}): string {
  return [invite.visitor_phone ?? invite.reference, fmtTime(invite.valid_from)]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Home widget: upcoming visitor invites (preview) and a quick-invite rail by
 * access type. Hidden when access management isn't licensed.
 */
export function AccessManagementWidget({ config }: WidgetProps): JSX.Element | null {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const accessEnabled = useModuleStore((s) => s.isModuleEnabled('access_management'));

  const [newOpen, setNewOpen] = useState(false);
  const [pendingEntryTypeId, setPendingEntryTypeId] = useState<string | undefined>();
  const [reinviteSource, setReinviteSource] = useState<AccessInvite | null>(null);

  const invitesQuery = useQuery({
    queryKey: ACCESS_INVITES_QUERY_KEY,
    queryFn: fetchAccessInvites,
    enabled: accessEnabled,
  });
  const typesQuery = useQuery({
    queryKey: ['access-entry-types'],
    queryFn: fetchAccessEntryTypesForInvite,
    enabled: accessEnabled,
  });

  const cancelMutation = useCancelAccessInviteMutation();
  const { requestCancel: promptCancelInvite, cancelConfirmSheet } =
    useAccessInviteCancelConfirm({
      onConfirm: (accessInviteId) => cancelMutation.mutateAsync(accessInviteId),
      loading: cancelMutation.isPending,
    });

  const upcoming = useMemo(
    () =>
      filterUpcomingInvites(invitesQuery.data ?? [], {
        includeAccepted: false,
        limit: WIDGET_INVITE_LIMIT,
      }),
    [invitesQuery.data],
  );
  const upcomingTotal = useMemo(
    () =>
      filterUpcomingInvites(invitesQuery.data ?? [], { includeAccepted: false })
        .length,
    [invitesQuery.data],
  );

  const reinviteFromWidget = (invite: AccessInvite) => {
    setPendingEntryTypeId(undefined);
    setReinviteSource(invite);
    setNewOpen(true);
  };

  if (!accessEnabled) return null;

  const entryTypes = typesQuery.data ?? [];
  const overflowCount = Math.max(0, upcomingTotal - upcoming.length);

  const openNewInvite = (entryTypeId?: string) => {
    setReinviteSource(null);
    setPendingEntryTypeId(entryTypeId);
    setNewOpen(true);
  };

  const closeNewInvite = (open: boolean) => {
    setNewOpen(open);
    if (!open) {
      setReinviteSource(null);
      setPendingEntryTypeId(undefined);
    }
  };

  return (
    <>
      <WidgetCard
        title={widgetTitle(config)}
        actionLabel="Manage"
        onAction={() => navigate('/app/access-invites')}
      >
        <p className="mb-2 text-[0.6875rem] font-semibold uppercase tracking-[0.16em] text-brass">
          Upcoming
        </p>
        {invitesQuery.isLoading ? (
          <Skeleton className="mb-4 h-14 rounded-xl" />
        ) : upcoming.length > 0 ? (
          <div className="mb-4 flex flex-col gap-2">
            {upcoming.map((invite) => {
              const meta = inviteMeta(invite);
              return (
                <SwipeableRow
                  key={invite.access_invite_id}
                  actions={buildInviteSwipeActions(invite, {
                    onAgain: reinviteFromWidget,
                    onCancel: promptCancelInvite,
                  })}
                >
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/app/access-invites?invite=${invite.access_invite_id}`)
                    }
                    className="flex w-full min-w-0 items-center gap-3 rounded-xl bg-sunken px-3 py-2.5 text-left transition-colors active:bg-surface"
                  >
                    <AccessEntryTypeIcon
                      accessEntryTypeId={invite.access_entry_type_id}
                      entryTypes={entryTypes}
                      tone="default"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-display text-body-sm font-semibold text-ink">
                        {invite.visitor_name?.trim() || 'Visitor'}
                      </p>
                      {meta ? (
                        <p className="truncate text-caption text-ink-muted">{meta}</p>
                      ) : null}
                    </div>
                  </button>
                </SwipeableRow>
              );
            })}
            {overflowCount > 0 ? (
              <button
                type="button"
                onClick={() => navigate('/app/access-invites')}
                className="text-center text-caption font-semibold text-primary-700"
              >
                +{overflowCount} more, view all
              </button>
            ) : null}
          </div>
        ) : (
          <p className="mb-4 rounded-xl bg-sunken px-4 py-3 text-center text-caption text-ink-secondary">
            No upcoming visitor invites.
          </p>
        )}

        <p className="mb-2.5 text-[0.6875rem] font-semibold uppercase tracking-[0.16em] text-brass">
          Invite a visitor
        </p>
        {typesQuery.isLoading ? (
          <div className="-mx-1 flex gap-2.5 overflow-x-auto px-1">
            <Skeleton className="h-[5.75rem] w-[5.25rem] shrink-0 rounded-xl" />
            <Skeleton className="h-[5.75rem] w-[5.25rem] shrink-0 rounded-xl" />
          </div>
        ) : entryTypes.length === 0 ? (
          <p className="rounded-xl bg-sunken px-4 py-3 text-center text-caption text-ink-secondary">
            No access types available to you.
          </p>
        ) : (
          <div className="-mx-1 flex gap-2.5 overflow-x-auto px-1 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {entryTypes.map((type) => (
                <div key={type.access_entry_type_id} className="w-[5.25rem] shrink-0">
                  <button
                    type="button"
                    onClick={() => openNewInvite(type.access_entry_type_id)}
                    className={homeTileButtonClass}
                  >
                    <AccessEntryTypeQuickIcon entryType={type} />
                    <TileLabel label={type.name} />
                  </button>
                </div>
              ))}
          </div>
        )}
      </WidgetCard>

      <NewAccessInviteSheet
        open={newOpen}
        onOpenChange={closeNewInvite}
        initialEntryTypeId={pendingEntryTypeId}
        reinviteFrom={reinviteSource}
        onCreated={() => {
          closeNewInvite(false);
          void queryClient.invalidateQueries({ queryKey: ACCESS_INVITES_QUERY_KEY });
        }}
      />
      {cancelConfirmSheet}
    </>
  );
}
